import React, {
  MouseEvent, useCallback, useEffect, useState,
} from 'react';
import RazemLogo from '../icons/RazemLogo';
import { Chevron, Close, Hamburger } from '../components/Icons';
import { Button, ButtonModes } from '../components/Button';

const classNames = require('html-classnames');

export type MenuItems = Array<MenuItem>;

export interface MenuItem {
  label: string,
  url: string | undefined,
  subMenu?: MenuItems,
}

export interface HeaderMenuProps {
  mainMenu: MenuItems,
  language: string,
}

const getLevelPosition = (isOpen: boolean, item: any, openedItem: any) => {
  if (!isOpen) {
    return 'in-front';
  }

  switch (true) {
    // Item's submenu is active menu.
    case item?.subMenu === openedItem:
      return 'behind';
    // Item is opened.
    case item === openedItem:
      return 'active';
    // Cases just for 0-level:
    // No submenu opened yet
    case !openedItem && item === null:
      return 'active';
    case openedItem && item === null:
      return 'behind';
    default:
      return 'in-front';
  }
};

const HeaderMenu: React.FC<HeaderMenuProps> = ({ mainMenu, language }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentItem, setCurrentItem] = useState<any>(null);
  const handleToggle = useCallback(() => {
    setCurrentItem(null);
    setIsOpen(!isOpen);
  }, [isOpen]);

  const icon = isOpen ? <Close /> : <Hamburger />;
  const label = isOpen ? 'Zamknij' : 'Otwórz menu';

  const getCurrentLevelClasses = useCallback((level: number, item: any) => {
    const classes = classNames('header-menu__main__list', {
      [`--level-${level}`]: true,
      [`--position-${getLevelPosition(isOpen, item, currentItem)}`]: true,
      '--is-closed': !isOpen,
    });

    return classes;
  }, [isOpen, currentItem]);

  const handleNextLevel = useCallback((item: any) => (event: MouseEvent) => {
    event.preventDefault();
    setCurrentItem(item);
  }, []);

  useEffect(() => {
    // @ts-ignore
    const body = document.querySelector('body');
    if (!body) {
      console.warn('Cannot find body, cannot lock it while menu open.');
      return;
    }

    if (isOpen) {
      // @ts-ignore
      body.style.top = `-${window.scrollY}px`;
      body.style.position = 'fixed';
      body.style.maxWidth = '100%;';
      return;
    }

    const scrollY = body.style.top;
    body.style.position = '';
    body.style.top = '';
    body.style.maxWidth = '';
    // @ts-ignore
    window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
  }, [isOpen]);

  // Close on resize window
  useEffect(() => {
    const handler = () => setIsOpen(false);
    // @ts-ignore
    window.addEventListener('resize', handler);

    // @ts-ignore
    return () => window.removeEventListener('resize', handler);
  }, []);

  const homepageUrl = language === 'pl' ? '/' : `/home-${language}`;

  return (
    <nav
      className={classNames('header-menu', {
        '--is-open': isOpen,
        '--is-closed': !isOpen,
      })}
    >
      <div className="header-menu__main">
        <div className="header-menu__main__logo">
          <RazemLogo homepageUrl={homepageUrl} />
        </div>
        <div className="header-menu__main__ela">
          <a
            href="https://static.partiarazem.pl/razem-static/docu/ELA_PoliticalPlatform.pdf"
            target="_blank"
          >
            <img
              src="/images/uploads/ela_logo.webp"
              alt="Europejski Sojusz Lewicy dla ludzi i planety"
            />
          </a>
        </div>
        <div className="header-menu__main__mobile-actions">
          <button
            type="button"
            className={`header-menu__main__mobile-actions__toggle header-menu__main__mobile-actions__toggle--${icon}`}
            // Hidden because the list is always available for screen readers. This is noise then.
            aria-hidden="true"
            onClick={handleToggle}
          >
            {icon}
            <span className="sr-only">{label}</span>
          </button>
        </div>
        <ul className={getCurrentLevelClasses(0, null)}>
          {mainMenu.map((item) => (
            <li
              key={item.label}
              className={classNames('header-menu__main__list__item', {
                '--no-url': !item.url,
              })}
            >
              <a
                href={item.url}
                onClick={item.subMenu ? handleNextLevel(item) : undefined}
              >
                {item.label}
                {item.subMenu ? (
                  <Chevron />
                ) : null}
              </a>
              {item.subMenu ? (
                <ul className={getCurrentLevelClasses(1, item)}>
                  <li className="header-menu__main__list__item header-menu__main__list__item--back">
                    <a
                      href={item.url}
                      onClick={(event) => {
                        event.preventDefault();
                        setCurrentItem(null);
                      }}
                    >
                      <Chevron />
                      {item.label}
                    </a>
                  </li>
                  {item.subMenu.map((itemLevel1) => (
                    <li
                      key={itemLevel1.label}
                      className="header-menu__main__list__item"
                    >
                      <a
                        href={itemLevel1.url}
                      >
                        {itemLevel1.label}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
        {language === 'pl' && (
          <div className="header-menu__main__donate">
            <Button
              mode={ButtonModes.primary}
              url="https://wesprzyj.partiarazem.pl"
              newWindow
            >
              Wspieram
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default HeaderMenu;
